import { dequal as deepEqual } from 'dequal'
import React, { memo } from 'react'

import { flexRender, functionalUpdate, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { FaAngleDown, FaAngleUp } from 'react-icons/fa'

import { Box, Flex, Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'

import DoubleScrollbar from '@/components/Elements/Scrollbar/DoubleScrollbar'
import { isNullish } from '@/utils/helper'
import { camelToWords } from '@/utils/stringHelper'
import { TextCell } from '../components/Cell'
import TableFilter from '../components/Filter/TableFilter'
import TablePagination from '../components/TablePagination'
import TableToolbar from '../components/TableToolbar'

const defaultColumn = {
  cell: TextCell,
  size: 200,
  maxSize: 400,

  header: ({ header: { id } }) => {
    if (isNullish(id)) {
      return <span>(-)</span>
    }
    return <span>{camelToWords(id)}</span>
  },
}

export const tableInitialState = {
  columnSizing: {},
  columnSizingInfo: {
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    isResizingColumn: false,
    columnSizingStart: [],
  },
  rowSelection: {},
  rowPinning: {
    top: [],
    bottom: [],
  },
  expanded: {},
  grouping: [],
  sorting: [],
  columnFilters: [],
  columnPinning: {
    left: [],
    right: [],
  },
  columnOrder: [],
  columnVisibility: {},
  pagination: {
    pageIndex: 0,
    pageSize: 10,
  },
}

const TableBody = ({ table, handleRowClick }) => {
  return (
    <Tbody>
      {table.getRowModel().rows.map((row) => {
        return (
          <Tr key={row.id} onClick={() => handleRowClick(row)}>
            {row.getVisibleCells().map((cell) => (
              <Td
                key={cell.id}
                p={2}
                textAlign={'left'}
                h="50px"
                overflow="hidden"
                textOverflow={'ellipsis'}
                whiteSpace={'nowrap'}
              >
                <Box w="100%" maxW="400px">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Box>
              </Td>
            ))}
          </Tr>
        )
      })}
    </Tbody>
  )
}

const MemoizedTableBody = memo(TableBody, (prev, next) => prev.table.options.data === next.table.options.data)

const FullTable = ({
  columnDef,
  data,
  rowCount,
  state,
  setState,
  handleRowClick,
  enableRowSelection,
  isLoading = false,
}) => {
  const tableInstance = useReactTable({
    data,
    rowCount: rowCount,
    columns: columnDef,
    defaultColumn,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    //Select
    enableRowSelection: enableRowSelection,

    //Pagination
    autoResetPageIndex: false,

    manualPagination: true,
    //Resize
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',

    //Filter
    manualFiltering: true,
    manualSorting: true,

    //Debug
    // debugTable: true,
    // debugHeaders: true,
  })

  tableInstance.setOptions((prev) => ({
    ...prev,
    state: state,

    onStateChange: (updater) => {
      const newState = functionalUpdate(updater, state)

      if (
        newState?.columnFilters &&
        state?.columnFilters &&
        !deepEqual(newState?.columnFilters, state?.columnFilters)
      ) {
        setState({
          ...newState,
          pagination: {
            pageIndex: 0,
            pageSize: newState?.pagination?.pageSize ?? 10,
          },
        })
      } else {
        setState(newState)
      }

      // setState(newState)
    },
    // onStateChange: setState,
  }))

  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const columnSizeVars = React.useMemo(() => {
    const headers = tableInstance.getFlatHeaders()
    const colSizes = {}
    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]
      colSizes[`--header-${header.id}-size`] = header.getSize()
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize()
    }
    return colSizes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableInstance.getState().columnSizingInfo, tableInstance.getState().columnSizing])

  const showMemorizedTable = tableInstance.getState().columnSizingInfo.isResizingColumn
  const showLoading = !showMemorizedTable && isLoading
  const showNormalTable = !showMemorizedTable && !isLoading
  return (
    <Box>
      <TableToolbar table={tableInstance}></TableToolbar>

      <Flex mt="3">
        <TablePagination table={tableInstance} dataCount={tableInstance.getPageCount()} />
      </Flex>
      <DoubleScrollbar>
        <Box minH="500px">
          <Table
            colorScheme="gray"
            variant="striped"
            border="1px solid rgb(226, 232, 240)"
            borderRadius="md"
            w={tableInstance.getCenterTotalSize()}
            minW={'100%'}
          >
            <Thead>
              {tableInstance.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Th
                      key={header.id}
                      p={2}
                      textAlign={'left'}
                      colSpan={header.colSpan}
                      w={header.getSize()}
                      h={'50px'}
                      position={'relative'}
                      role={'group'}
                    >
                      <Flex h="100%" direction="column" justifyContent="space-between">
                        <Flex _hover={{ cursor: 'pointer' }} w="100%" onClick={header.column.getToggleSortingHandler()}>
                          <Box w="100%"> {flexRender(header.column.columnDef.header, header.getContext())}</Box>

                          {/* Sorting */}
                          {{ asc: <FaAngleUp />, desc: <FaAngleDown /> }[`${header.column.getIsSorted()}`] ?? null}
                        </Flex>

                        {/* Filter */}
                        {header.column.getCanFilter() ? (
                          <Box mt="1">
                            <TableFilter
                              column={header.column}
                              updatePagination={() => tableInstance.setPageIndex(0)}
                            />
                          </Box>
                        ) : null}
                      </Flex>

                      {/* Resize Handler */}
                      <Box
                        position={'absolute'}
                        top={0}
                        right={0}
                        w="5px"
                        height={'100%'}
                        bg={header.column.getIsResizing() ? 'gray.400' : ''}
                        _groupHover={{
                          bg: header.column.getIsResizing() ? 'gray.400' : 'teal.400',
                          cursor: 'col-resize',
                        }}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        onDoubleClick={() => header.column.resetSize()}
                      />
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>

            {showMemorizedTable && <MemoizedTableBody table={tableInstance} handleRowClick={handleRowClick} />}

            {showNormalTable && <TableBody table={tableInstance} handleRowClick={handleRowClick} />}

            {tableInstance.getFooterProps && (
              <Tfoot>
                <Tr>
                  {tableInstance.getFooterProps().map((footer) => (
                    <Td key={footer.id} p={2} textAlign={'left'} w="300px">
                      {flexRender(footer.column.columnDef.footer, footer.getContext())}
                    </Td>
                  ))}
                </Tr>
              </Tfoot>
            )}
          </Table>
        </Box>
      </DoubleScrollbar>
      <Flex mt="3">
        <TablePagination table={tableInstance} dataCount={tableInstance.getPageCount()} />
      </Flex>
    </Box>
  )
}

export default FullTable
